import classNames from 'classnames';
import { ChangeEvent, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { boolean, date, InferType, mixed, number, object } from 'yup';

import { AddressValue, BuildingType, EnergyLabel, StandardType, useGetSourcesAddressesSearchQuery } from '@client/api';
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  combineStrings,
  DatepickerV2,
  DistanceAdornment,
  FilterButton,
  FormField,
  FormFieldRenderProps,
  FormSubmitButton,
  getCountries,
  GridCol,
  GridRow,
  HelperText,
  Input,
  isDefinedString,
  mergeObjects,
  TagAutocomplete,
  Text,
  transformToOptionalNumber,
  transformToOptionalObject,
  useSearching,
} from '@client/toolkit';

import { BuildingsTab } from './buildings-tab.component';

import { useBuildingsFilterQueryParams } from '../hooks/use-buildings-query-params';
import dayjs from 'dayjs';
import { BuildingCreateCta } from './building-create-cta';

const COUNTRIES = getCountries();

// schema will not be used for validation but for setting up the filter data structure and to handle the form fields
const filterSchema = object({
  locationFromEnabled: boolean().required().default(false),
  locationFrom: mixed<AddressValue>().transform(transformToOptionalObject).optional(),
  locationRadius: number().transform(transformToOptionalNumber).optional().default(10),

  totalAreaEnabled: boolean().required().default(false),
  totalAreaFrom: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),
  totalAreaTo: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),

  availableAreaEnabled: boolean().required().default(false),
  availableAreaFrom: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),
  availableAreaTo: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),

  unavailableAreaEnabled: boolean().required().default(false),
  unavailableAreaFrom: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),
  unavailableAreaTo: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),

  availableDateEnabled: boolean().required().default(false),
  availableDateFrom: date()
    .optional()
    .default('' as any),
  availableDateTo: date()
    .optional()
    .default('' as any),

  vacantDateEnabled: boolean().required().default(false),
  vacantDateFrom: date()
    .optional()
    .default('' as any),
  vacantDateTo: date()
    .optional()
    .default('' as any),

  constructionYearEnabled: boolean().required().default(false),
  constructionYearFrom: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),
  constructionYearTo: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),

  bedroomCountEnabled: boolean().required().default(false),
  bedroomCountFrom: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),
  bedroomCountTo: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),

  bathroomCountEnabled: boolean().required().default(false),
  bathroomCountFrom: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),
  bathroomCountTo: number()
    .transform(transformToOptionalNumber)
    .optional()
    .default('' as any),

  energyLabel: mixed<Array<EnergyLabel>>().optional().default([]),
  standard: mixed<Array<StandardType>>().optional().default([]),
  buildingTypes: mixed<Array<BuildingType>>().optional().default([]),

  tags: mixed<Array<string>>().optional().default([]),
});

type FormValues = InferType<typeof filterSchema>;
type FormPath = FieldPath<FormValues>;
type FormDefaultValues = DeepPartial<FormValues>;

const schemaDefaultValues = filterSchema.getDefault();

const STANDARD_TYPE_OPTIONS: StandardType[] = ['Low', 'Medium', 'High'];
const ENERGY_LABEL_OPTIONS: EnergyLabel[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

const BUILDING_TYPE_OPTIONS: BuildingType[] = ['Office', 'RetailHighStreet', 'Retail', 'Logistics', 'Industrial', 'Hotel', 'Healthcare', 'Education', 'Mixed'];

function FilterLabel({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <Text as="span" className={classNames('block text-sm font-bold', className)}>
      {children}
    </Text>
  );
}

export function BuildingsFilter() {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  const [queryParams, setQueryParam] = useBuildingsFilterQueryParams();

  const defaultValues: FormDefaultValues = useMemo(
    () => ({ ...mergeObjects(schemaDefaultValues, queryParams) }),
    [queryParams],
  );

  const methods = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues,
  });

  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    getFieldState,
    formState: { defaultValues: currentDefaultValues, isDirty },
  } = methods;

  const locationFrom = watch('locationFrom');
  const locationRadiusEnabled = watch('locationFromEnabled');

  const energyLabel = watch('energyLabel');
  const energyLabelEnabled = useMemo(
    () => (Array.isArray(energyLabel) ? energyLabel.length > 0 : false),
    [energyLabel],
  );

  const standard = watch('standard');
  const standardEnabled = useMemo(() => (Array.isArray(standard) ? standard.length > 0 : false), [standard]);

  const buildingTypes = watch('buildingTypes');
  const buildingTypeEnabled = useMemo(
    () => (Array.isArray(buildingTypes) ? buildingTypes.length > 0 : false),
    [buildingTypes],
  );

  const totalAreaEnabled = watch('totalAreaEnabled');
  const availableAreaEnabled = watch('availableAreaEnabled');
  const unavailableAreaEnabled = watch('unavailableAreaEnabled');
  const availableDateEnabled = watch('availableDateEnabled');
  const vacantDateEnabled = watch('vacantDateEnabled');
  const constructionYearEnabled = watch('constructionYearEnabled');

  const bedroomCountEnabled = watch('bedroomCountEnabled');
  const bathroomCountEnabled = watch('bathroomCountEnabled');

  const tags = watch('tags');
  const tagsEnabled = useMemo(() => (Array.isArray(tags) ? tags.length > 0 : false), [tags]);

  const isFilterSet = useMemo(() => {
    return (
      isDirty ||
      locationRadiusEnabled ||
      standardEnabled ||
      totalAreaEnabled ||
      availableAreaEnabled ||
      unavailableAreaEnabled ||
      availableDateEnabled ||
      vacantDateEnabled ||
      constructionYearEnabled ||
      energyLabelEnabled ||
      buildingTypeEnabled ||
      bedroomCountEnabled ||
      bathroomCountEnabled ||
      tagsEnabled
    );
  }, [
    isDirty,
    locationRadiusEnabled,
    standardEnabled,
    totalAreaEnabled,
    availableAreaEnabled,
    unavailableAreaEnabled,
    availableDateEnabled,
    vacantDateEnabled,
    constructionYearEnabled,
    energyLabelEnabled,
    buildingTypeEnabled,
    bedroomCountEnabled,
    bathroomCountEnabled,
    tagsEnabled,
  ]);

  const toggleFilterOpen = () => {
    if (isFilterOpen) {
      reset(currentDefaultValues);
    } // reset filter to currently applied state if closing the filter
    setIsFilterOpen(!isFilterOpen);
  };

  const isOptionInArray = (field: FormFieldRenderProps<FormValues>, option: any) => {
    return Array.isArray(field.value) ? (field as { value: any[] }).value.includes(option) : false;
  };

  const handleChangeMultiSelect = (event: ChangeEvent<HTMLInputElement>, field: FormFieldRenderProps<any>) => {
    if (!Array.isArray(field.value)) return;

    if (event.target.checked) {
      field.onChange([...field.value, event.target.value]);
    } else {
      field.onChange(field.value.filter((value) => value !== event.target.value));
    }
  };

  const handleChangeInputFromToRange = (
    event: ChangeEvent<HTMLInputElement>,
    field: FormFieldRenderProps<FormValues>,
    name: keyof FormValues,
  ) => {
    setValue(name, true);
    field.onChange(event);
  };

  const handleChangeDateFromToRange = (
    date: Date | null,
    field: FormFieldRenderProps<FormValues>,
    name: keyof FormValues,
  ) => {
    setValue(name, true);
    field.onChange(date ?? ''); // if cleared the date will be null
  };

  const resetFilter = () => {
    reset(schemaDefaultValues);

    // we need to explicitly set all query params to `undefined` in order to get them removed from the url
    const unsetValues: Record<string, unknown> = { ...schemaDefaultValues };
    Object.keys(unsetValues).forEach((prop) => {
      if (unsetValues[prop] == null) {
        return;
      }
      unsetValues[prop] = undefined;
    });
    setQueryParam(unsetValues);
  };

  const applyFilter = (data: FormValues) => {
    // we need to cleanup the data in order to remove empty strings, empty array, false boolean value etc. otherwise the query string will include everything (although filter wasn't used)
    const dataFiltered: Record<string, unknown> = { ...data };
    Object.keys(dataFiltered).forEach((prop) => {
      if (dataFiltered[prop] == null) {
        return;
      }
      const value = dataFiltered[prop];
      if (typeof value === 'string') {
        if (value.trim() === '' || value == null) {
          dataFiltered[prop] = undefined;
        }
      } else if (Array.isArray(value)) {
        if (value.length <= 0) {
          dataFiltered[prop] = undefined;
        }
      } else if (typeof value === 'boolean') {
        if (value === false) {
          dataFiltered[prop] = undefined;
        }
      }
    });

    reset(data);
    setQueryParam(dataFiltered);
  };

  const getAddressLabel = useCallback((address?: AddressValue | null) => {
    const street = address?.streetAddress1;
    const city = address?.municipality;
    const country = COUNTRIES.find((x) => x.alpha3 === address?.country.threeLetterCode)?.name;

    return combineStrings(', ', street, city, country) ?? '';
  }, []);

  const { debouncedSearch, updateSearch, searchDebouncing } = useSearching();
  const { currentData: addressData, isFetching } = useGetSourcesAddressesSearchQuery(
    { query: debouncedSearch ?? '' },
    { skip: !isDefinedString(debouncedSearch) },
  );

  const addressSearchResult = useMemo(() => addressData ?? [], [addressData]);
  const [selectedAddress, setSelectedAddress] = useState<AddressValue | null>(locationFrom ?? null);

  const handleSelectAddress = useCallback(
    (address: AddressValue | null) => {
      setSelectedAddress(address);

      setValue('locationFromEnabled', !!address, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue('locationFrom', address ?? undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const setTagsToFormValues = useCallback(
    (e: CustomEvent<Tagify.ChangeEventData>) => {
      const tags = e.detail.tagify.getCleanValue().map((tag) => tag.value);
      setValue('tags', tags, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  return (
    <>
      <div className="flex items-stretch space-x-2">
        <BuildingsTab />

{/*        <FilterButton
          onClick={() => toggleFilterOpen()}
          onResetFilter={() => resetFilter()}
          active={isFilterSet}
          textActive={tCommon('filterActive')}
          textInactive={tCommon('filterInactive')}
        >
          {tCommon('filter')}
        </FilterButton> */}
        <div className="flex-1" />
        <BuildingCreateCta />
      </div>

      {isFilterOpen && (
        <Card className="bg-surface-base backdrop-blur-lg text-white">
          <FormProvider {...methods}>
            <form noValidate onSubmit={handleSubmit(applyFilter)}>
              <div className="m-2 px-4 py-4 rounded text-sm">
                <div className="mb-2">
                  <span className="font-bold text-xl">{tCommon('filter', { context: 'plural' })}</span>
                </div>
                <div className="py-4">
                  {/* LOCATION */}
                  <GridRow cols={12} className="gap-2">
                    <GridCol span={4} className="space-y-2">
                      <FilterLabel className="mb-2">{tCommon('location')}</FilterLabel>
                    </GridCol>

                    <GridCol span={2} className="space-y-2">
                      <FilterLabel className="mb-2">{tCommon('radius')}</FilterLabel>
                    </GridCol>
                  </GridRow>

                  <GridRow cols={12} className="gap-2">
                    <GridCol span={4}>
                      <Autocomplete<AddressValue | null>
                        selected={selectedAddress}
                        getSelectedLabel={getAddressLabel}
                        options={addressSearchResult ?? []}
                        getOptionLabel={getAddressLabel}
                        onChange={handleSelectAddress}
                        onInput={updateSearch}
                        dirty={getFieldState('locationFrom').isDirty}
                        error={!!getFieldState('locationFrom').error?.message}
                        loading={isFetching || searchDebouncing}
                      />
                      {getFieldState('locationFrom').error?.message && (
                        <HelperText className={'mt-1'} error={!!getFieldState('locationFrom').error?.message}>
                          {getFieldState('locationFrom').error?.message}
                        </HelperText>
                      )}
                    </GridCol>

                    <GridCol span={2}>
                      <FormField<FormValues, FormPath>
                        name="locationRadius"
                        render={({ field }) => (
                          <DistanceAdornment>
                            <Input type="number" placeholder="0" className="pr-10" {...field} />
                          </DistanceAdornment>
                        )}
                      />
                    </GridCol>
                  </GridRow>

                  <GridRow cols={12} spacing={12} className="mt-4">
                    <GridCol span={6} className="space-y-8">
                      {/* AREA SIZE */}
                      <div>
                        <FilterLabel className="mb-2">{tCommon('area', { context: 'plural' })}</FilterLabel>

                        <div className="space-y-2">
                          <Checkbox
                            className="mr-2"
                            checked={
                              !watch('totalAreaEnabled') &&
                              !watch('availableAreaEnabled') &&
                              !watch('unavailableAreaEnabled')
                            }
                            onChange={() => {
                              setValue('totalAreaEnabled', false, { shouldDirty: true });
                              setValue('availableAreaEnabled', false, { shouldDirty: true });
                              setValue('unavailableAreaEnabled', false, { shouldDirty: true });
                            }}
                          >
                            <div className="ml-2 mr-2">{tCommon('any')}</div>
                          </Checkbox>

                          <GridRow spacing={2} cols={12}>
                            <GridCol span={4} className="flex">
                              <FormField<FormValues, FormPath>
                                name="totalAreaEnabled"
                                render={({ field }) => (
                                  <Checkbox className="mr-2" {...field} checked={field.value === true}>
                                    <div className="ml-2 mr-2">{tGlobal('total', { context: 'short' })}</div>
                                  </Checkbox>
                                )}
                              />
                            </GridCol>

                            <GridCol span={8} className="flex space-x-1">
                              <FormField<FormValues, FormPath>
                                name="totalAreaFrom"
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    size="small"
                                    placeholder={tCommon('from')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'totalAreaEnabled')}
                                  />
                                )}
                              />
                              <div>-</div>
                              <FormField<FormValues, FormPath>
                                name="totalAreaTo"
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    size="small"
                                    placeholder={tCommon('to')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'totalAreaEnabled')}
                                  />
                                )}
                              />
                            </GridCol>
                          </GridRow>

                          <GridRow spacing={2} cols={12}>
                            <GridCol span={4} className="flex">
                              <FormField<FormValues, FormPath>
                                name="availableAreaEnabled"
                                render={({ field }) => (
                                  <Checkbox className="mr-2" {...field} checked={field.value === true}>
                                    <div className="ml-2 mr-2">{tGlobal('available', { context: 'short' })}</div>
                                  </Checkbox>
                                )}
                              />
                            </GridCol>

                            <GridCol span={8} className="flex space-x-1">
                              <FormField<FormValues, FormPath>
                                name="availableAreaFrom"
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    size="small"
                                    placeholder={tCommon('from')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'availableAreaEnabled')}
                                  />
                                )}
                              />
                              <div>-</div>
                              <FormField<FormValues, FormPath>
                                name="availableAreaTo"
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    size="small"
                                    placeholder={tCommon('to')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'availableAreaEnabled')}
                                  />
                                )}
                              />
                            </GridCol>
                          </GridRow>

                          {/*   <GridRow spacing={2} cols={12}>
                            <GridCol span={5} className="flex">
                              <FormField<FormValues, FormPath> name="unavailableAreaEnabled" render={({ field }) => (
                                <Checkbox className="mr-2" {...field} checked={field.value === true}>
                                  <div className="ml-2 mr-2">{tGlobal('buildingUnit.unavailableAreaSize', { context: 'short' })}</div>
                                </Checkbox>
                              )} />
                            </GridCol>

                            <GridCol span={7} className="flex space-x-1">
                              <FormField<FormValues, FormPath> name="unavailableAreaFrom" render={({ field }) => (
                                <Input type="number" size="small" placeholder={tCommon('from')} {...field} onChange={(e) => handleChangeFromToRange(e, field, 'unavailableAreaEnabled')} />
                              )} />
                              <div>-</div>
                              <FormField<FormValues, FormPath> name="unavailableAreaTo" render={({ field }) => (
                                <Input type="number" size="small" placeholder={tCommon('to')} {...field} onChange={(e) => handleChangeFromToRange(e, field, 'unavailableAreaEnabled')} />
                              )} />
                            </GridCol>
                          </GridRow> */}
                        </div>
                      </div>

                      {/* ROOM COUNT - commented out for commercial buildings
                      <div>
                        <FilterLabel className="mb-2">{tGlobal('entity.room', { context: 'plural' })}</FilterLabel>

                        <div className="space-y-2">
                          <Checkbox
                            className="mr-2"
                            checked={!bedroomCountEnabled && !bathroomCountEnabled}
                            onChange={() => {
                              setValue('bedroomCountEnabled', false, { shouldDirty: true });
                              setValue('bathroomCountEnabled', false, { shouldDirty: true });
                            }}
                          >
                            <div className="ml-2 mr-2">{tCommon('any')}</div>
                          </Checkbox>

                          <GridRow spacing={2} cols={12}>
                            <GridCol span={4} className="flex">
                              <FormField<FormValues, FormPath>
                                name="bedroomCountEnabled"
                                render={({ field }) => (
                                  <Checkbox className="mr-2" {...field} checked={field.value === true}>
                                    <div className="ml-2 mr-2">{tType('RoomType.Bedroom', { context: 'plural' })}</div>
                                  </Checkbox>
                                )}
                              />
                            </GridCol>

                            <GridCol span={8} className="flex space-x-1">
                              <FormField<FormValues, FormPath>
                                name="bedroomCountFrom"
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    size="small"
                                    placeholder={tCommon('from')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'bedroomCountEnabled')}
                                  />
                                )}
                              />
                              <div>-</div>
                              <FormField<FormValues, FormPath>
                                name="bedroomCountTo"
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    size="small"
                                    placeholder={tCommon('to')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'bedroomCountEnabled')}
                                  />
                                )}
                              />
                            </GridCol>
                          </GridRow>

                          <GridRow spacing={2} cols={12}>
                            <GridCol span={4} className="flex">
                              <FormField<FormValues, FormPath>
                                name="bathroomCountEnabled"
                                render={({ field }) => (
                                  <Checkbox className="mr-2" {...field} checked={field.value === true}>
                                    <div className="ml-2 mr-2">{tType('RoomType.Bathroom', { context: 'plural' })}</div>
                                  </Checkbox>
                                )}
                              />
                            </GridCol>

                            <GridCol span={8} className="flex space-x-1">
                              <FormField<FormValues, FormPath>
                                name="bathroomCountFrom"
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    size="small"
                                    placeholder={tCommon('from')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'bathroomCountEnabled')}
                                  />
                                )}
                              />
                              <div>-</div>
                              <FormField<FormValues, FormPath>
                                name="bathroomCountTo"
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    size="small"
                                    placeholder={tCommon('to')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'bathroomCountEnabled')}
                                  />
                                )}
                              />
                            </GridCol>
                          </GridRow>
                        </div>
                      </div>
                      */}

                      {/* AVAILABLE DATE */}
                      <div>
                        <FilterLabel className="mb-2">{tGlobal('space.availableDate')}</FilterLabel>

                        <div className="space-y-2">
                          <Checkbox
                            className="mr-2"
                            checked={!watch('availableDateEnabled')}
                            onChange={() => {
                              setValue('availableDateEnabled', false, { shouldDirty: true });
                            }}
                          >
                            <div className="ml-2 mr-2">{tCommon('any')}</div>
                          </Checkbox>

                          <GridRow spacing={2} cols={12}>
                            <GridCol span={4} className="flex">
                              <FormField<FormValues, FormPath>
                                name="availableDateEnabled"
                                render={({ field }) => (
                                  <Checkbox className="mr-2" {...field} checked={field.value === true}>
                                    <div className="ml-2 mr-2">{tCommon('range')}</div>
                                  </Checkbox>
                                )}
                              />
                            </GridCol>

                            <GridCol span={8} className="flex space-x-1 items-center">
                              <FormField<FormValues, FormPath>
                                name="availableDateFrom"
                                render={({ field: { value, ...field } }) => (
                                  <DatepickerV2
                                    label={tCommon('from')}
                                    iconSize={24}
                                    {...field}
                                    onChange={(date, e) =>
                                      handleChangeDateFromToRange(
                                        date?.toDate() ?? null,
                                        { value, ...field },
                                        'availableDateEnabled',
                                      )
                                    }
                                  />
                                )}
                              />
                              <div>-</div>
                              <FormField<FormValues, FormPath>
                                name="availableDateTo"
                                render={({ field: { value, ...field } }) => (
                                  <DatepickerV2
                                    label={tCommon('to')}
                                    iconSize={24}
                                    {...field}
                                    onChange={(date, e) =>
                                      handleChangeDateFromToRange(
                                        date?.toDate() ?? null,
                                        { value, ...field },
                                        'availableDateEnabled',
                                      )
                                    }
                                  />
                                )}
                              />
                            </GridCol>
                          </GridRow>
                        </div>
                      </div>

                      {/* VACANT DATE */}
                      <div>
                        <FilterLabel className="mb-2">{tGlobal('space.vacantDate')}</FilterLabel>

                        <div className="space-y-2">
                          <Checkbox
                            className="mr-2"
                            checked={!watch('vacantDateEnabled')}
                            onChange={() => {
                              setValue('vacantDateEnabled', false, { shouldDirty: true });
                            }}
                          >
                            <div className="ml-2 mr-2">{tCommon('any')}</div>
                          </Checkbox>

                          <GridRow spacing={2} cols={12}>
                            <GridCol span={4} className="flex">
                              <FormField<FormValues, FormPath>
                                name="vacantDateEnabled"
                                render={({ field }) => (
                                  <Checkbox className="mr-2" {...field} checked={field.value === true}>
                                    <div className="ml-2 mr-2">{tCommon('range')}</div>
                                  </Checkbox>
                                )}
                              />
                            </GridCol>

                            <GridCol span={8} className="flex space-x-1 items-center">
                              <FormField<FormValues, FormPath>
                                name="vacantDateFrom"
                                render={({ field: { value, ...field } }) => (
                                  <DatepickerV2
                                    label={tCommon('from')}
                                    iconSize={24}
                                    {...field}
                                    onChange={(date, e) =>
                                      handleChangeDateFromToRange(
                                        date?.toDate() ?? null,
                                        { value, ...field },
                                        'vacantDateEnabled',
                                      )
                                    }
                                  />
                                )}
                              />
                              <div>-</div>
                              <FormField<FormValues, FormPath>
                                name="vacantDateTo"
                                render={({ field: { value, ...field } }) => (
                                  <DatepickerV2
                                    label={tCommon('to')}
                                    iconSize={24}
                                    {...field}
                                    onChange={(date, e) =>
                                      handleChangeDateFromToRange(
                                        date?.toDate() ?? null,
                                        { value, ...field },
                                        'vacantDateEnabled',
                                      )
                                    }
                                  />
                                )}
                              />
                            </GridCol>
                          </GridRow>
                        </div>
                      </div>

                      {/* CONSTRUCTION YEAR */}
                      <div>
                        <FilterLabel className="mb-2">{tGlobal('building.constructionYear')}</FilterLabel>

                        <div className="space-y-2">
                          <Checkbox
                            className="mr-2"
                            checked={!constructionYearEnabled}
                            onChange={() => setValue('constructionYearEnabled', false, { shouldDirty: true })}
                          >
                            <div className="ml-2 mr-2">{tCommon('any')}</div>
                          </Checkbox>

                          <GridRow spacing={2} cols={12}>
                            <GridCol span={4} className="flex">
                              <FormField<FormValues, FormPath>
                                name="constructionYearEnabled"
                                render={({ field }) => (
                                  <Checkbox className="mr-2" {...field} checked={field.value === true}>
                                    <div className="ml-2 mr-2">{tCommon('range', { context: 'short' })}</div>
                                  </Checkbox>
                                )}
                              />
                            </GridCol>

                            <GridCol span={8} className="flex space-x-1">
                              <FormField<FormValues, FormPath>
                                name="constructionYearFrom"
                                render={({ field }) => (
                                  <Input
                                    size="small"
                                    placeholder={tCommon('from')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'constructionYearEnabled')}
                                  />
                                )}
                              />
                              <div>-</div>
                              <FormField<FormValues, FormPath>
                                name="constructionYearTo"
                                render={({ field }) => (
                                  <Input
                                    size="small"
                                    placeholder={tCommon('to')}
                                    {...field}
                                    onChange={(e) => handleChangeInputFromToRange(e, field, 'constructionYearEnabled')}
                                  />
                                )}
                              />
                            </GridCol>
                          </GridRow>
                        </div>
                      </div>

                      {/* TAGS */}
                      <div>
                        <FilterLabel className="mb-2">{tGlobal('entity.tag', { count: 0 })}</FilterLabel>

                        <div className="space-y-2">
                          <FormField<FormValues, FormPath>
                            name="tags"
                            render={() => (
                              <TagAutocomplete
                                defaultValue={tags}
                                onChange={setTagsToFormValues}
                                queryParams={{ 'isUsed.Equal': true }}
                                settings={{ enforceWhitelist: true }}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </GridCol>

                    <GridCol span={2} className="space-y-8">
                      {/* BUILDING TYPE */}
                      <div>
                        <FilterLabel className="mb-2">{tGlobal('building.type')}</FilterLabel>

                        <div className="space-y-2">
                          <Checkbox
                            className="mr-2"
                            checked={!buildingTypeEnabled}
                            onChange={() => {
                              setValue('buildingTypes', [], { shouldDirty: true });
                            }}
                          >
                            <div className="ml-2 mr-2">{tCommon('any')}</div>
                          </Checkbox>

                          <FormField<FormValues, FormPath>
                            name="buildingTypes"
                            render={({ field }) => (
                              <>
                                {BUILDING_TYPE_OPTIONS.map((option) => (
                                  <Checkbox
                                    key={option}
                                    value={option}
                                    checked={isOptionInArray(field, option)}
                                    onChange={(e) => handleChangeMultiSelect(e, field)}
                                  >
                                    <div className="ml-2">{tType(`BuildingType.${option}`)}</div>
                                  </Checkbox>
                                ))}
                              </>
                            )}
                          />
                        </div>
                      </div>
                    </GridCol>

                    <GridCol span={2} className="space-y-8">
                      {/*ENERGY LABEL*/}
                      <div>
                        <FilterLabel className="mb-2">{tGlobal('building.energyLabel')}</FilterLabel>

                        <div className="space-y-2">
                          <Checkbox
                            className="mr-2"
                            checked={!energyLabelEnabled}
                            onChange={() => {
                              setValue('energyLabel', [], { shouldDirty: true });
                            }}
                          >
                            <div className="ml-2 mr-2">{tCommon('any')}</div>
                          </Checkbox>

                          <FormField<FormValues, FormPath>
                            name="energyLabel"
                            render={({ field }) => (
                              <>
                                {ENERGY_LABEL_OPTIONS.map((option) => (
                                  <Checkbox
                                    key={option}
                                    value={option}
                                    checked={isOptionInArray(field, option)}
                                    onChange={(e) => handleChangeMultiSelect(e, field)}
                                  >
                                    <div className="ml-2">{option}</div>
                                  </Checkbox>
                                ))}
                              </>
                            )}
                          />
                        </div>
                      </div>

                      {/* STANDARD TYPE */}
                      <div>
                        <FilterLabel className="mb-2">{tGlobal('space.standard')}</FilterLabel>

                        <div className="space-y-2">
                          <Checkbox
                            className="mr-2"
                            checked={!standardEnabled}
                            onChange={() => {
                              setValue('standard', [], { shouldDirty: true });
                            }}
                          >
                            <div className="ml-2 mr-2">{tCommon('any')}</div>
                          </Checkbox>

                          <FormField<FormValues, FormPath>
                            name="standard"
                            render={({ field }) => (
                              <>
                                {STANDARD_TYPE_OPTIONS.map((option) => (
                                  <Checkbox
                                    key={option}
                                    value={option}
                                    checked={isOptionInArray(field, option)}
                                    onChange={(e) => handleChangeMultiSelect(e, field)}
                                  >
                                    <div className="ml-2">{tType(`StandardType.${option}`)}</div>
                                  </Checkbox>
                                ))}
                              </>
                            )}
                          />
                        </div>
                      </div>
                    </GridCol>
                  </GridRow>
                </div>
              </div>
              <div className="flex justify-start px-4 py-4 space-x-2">
                <FormSubmitButton type="submit" fontSize="sm">
                  {tCommon('apply')}
                </FormSubmitButton>
                <Button fontSize="sm" color="primary-dark" onClick={() => toggleFilterOpen()}>
                  {tCommon('hide')}
                </Button>
                {isFilterSet && (
                  <Button fontSize="sm" color="primary-dark" onClick={() => resetFilter()}>
                    {tCommon('reset')}
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </Card>
      )}
    </>
  );
}
